import { FileText, Settings, MessageSquare, HelpCircle, Globe, LogOut, FileSpreadsheet, ArrowLeft, ArrowRight, Menu, X, Users } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Sidebar, SidebarContent, SidebarFooter, SidebarGroup, SidebarGroupLabel, SidebarGroupContent, SidebarMenu, SidebarMenuButton, SidebarMenuItem, SidebarTrigger } from "@/components/ui/sidebar";
import { useTranslation } from "react-i18next";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { useState, useEffect } from "react";

const languages = [{
  code: "en",
  name: "English"
}, {
  code: "pt",
  name: "Português"
}];

export function AppSidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { i18n, t } = useTranslation();
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);

  // Close mobile sidebar when route changes
  useEffect(() => {
    setIsMobileOpen(false);
  }, [location.pathname]);

  const items = [{
    title: t("navigation.schedule_invoices", "Schedule Invoice"),
    url: "/dashboard",
    icon: FileText
  }, {
    title: t("navigation.templates", "Templates"),
    url: "/templates",
    icon: FileSpreadsheet
  }, {
    title: t("navigation.customers", "Customers"),
    url: "/customers",
    icon: Users
  }];

  const footerItems = [{
    title: t("navigation.account_settings", "Account Settings"),
    url: "/account_settings",
    icon: Settings
  }, {
    title: t("navigation.contact_us", "Contact Us"),
    url: "/contact_us",
    icon: MessageSquare
  }, {
    title: t("navigation.faq", "FAQ"),
    url: "/faq",
    icon: HelpCircle
  }];

  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut({
        scope: 'local'
      });
      try {
        await supabase.auth.signOut({
          scope: 'global'
        });
      } catch (serverError) {
        console.warn("Server-side logout failed:", serverError);
      }
      navigate("/");
      toast.success(t("auth.signOutSuccess", "Signed out successfully"));
    } catch (error) {
      console.error("Sign out error:", error);
      navigate("/");
      toast.error(t("auth.signOutError", "Error during sign out: ") + (error instanceof Error ? error.message : "Unknown error occurred"));
    }
  };

  const handleNavigation = (url: string) => {
    navigate(url);
    setIsMobileOpen(false);
  };

  const isCurrentRoute = (path: string) => location.pathname === path;

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  return (
    <>
      {/* Mobile Menu Button - positioned at the top left of content area */}
      <div className="md:hidden fixed top-4 left-4 z-50">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setIsMobileOpen(!isMobileOpen)}
          aria-label={isMobileOpen ? "Close menu" : "Open menu"}
          className="bg-white/80 shadow-sm"
        >
          <Menu className="h-6 w-6" />
        </Button>
      </div>

      {/* Mobile Overlay */}
      {isMobileOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/20 z-40"
          onClick={() => setIsMobileOpen(false)}
        />
      )}

      {/* Mobile Sidebar */}
      <div
        className={`md:hidden fixed left-0 top-0 bottom-0 w-[268px] bg-[#34375B] z-40 transform transition-transform duration-300 ease-in-out ${isMobileOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
      >
        <div className="flex flex-col h-full">
          <div className="bg-[#8791f3] flex-1 overflow-y-auto">
            <div className="flex justify-end p-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsMobileOpen(false)}
                aria-label="Close menu"
              >
                <X className="h-6 w-6" />
              </Button>
            </div>

            <SidebarGroup className="pt-4">
              <SidebarGroupContent>
                <SidebarMenu>
                  <SidebarMenuItem>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <SidebarMenuButton className="w-full">
                          <Globe className="w-4 h-4" />
                          <span className="transition-opacity duration-200 hover:text-[#5E64A7]">
                            {languages.find(lang => lang.code === i18n.language)?.name || t("common.language", "Language")}
                          </span>
                        </SidebarMenuButton>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start">
                        {languages.map(lang => (
                          <DropdownMenuItem key={lang.code} onClick={() => i18n.changeLanguage(lang.code)}>
                            {lang.name}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </SidebarMenuItem>
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>

            <SidebarGroup className="mt-4">
              <SidebarGroupContent>
                <SidebarMenu>
                  {items.map(item => (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton
                        tooltip={item.title}
                        onClick={() => handleNavigation(item.url)}
                        className={`text-base hover:text-[#5E64A7] ${isCurrentRoute(item.url) ? 'text-[#34375B]' : ''}`}
                      >
                        <item.icon className="w-4 h-4" />
                        <span className="transition-opacity duration-200">{item.title}</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </div>

          <div className="bg-[#8791f3]">
            <SidebarGroup>
              <SidebarGroupContent>
                <SidebarMenu>
                  {footerItems.map(item => (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton
                        tooltip={t(item.title)}
                        onClick={() => handleNavigation(item.url)}
                        className={`text-base hover:text-[#5E64A7] ${isCurrentRoute(item.url) ? 'text-[#34375B]' : ''}`}
                      >
                        <item.icon className="w-4 h-4" />
                        <span className="transition-opacity duration-200">{t(item.title)}</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      tooltip={t("common.signOut", "Sign Out")}
                      onClick={handleSignOut}
                      className="text-base hover:text-[#5E64A7]"
                    >
                      <LogOut className="w-4 h-4" />
                      <span className="transition-opacity duration-200">{t("common.signOut", "Sign Out")}</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </div>
        </div>
      </div>

      {/* Desktop Sidebar */}
      <Sidebar
        className={`bg-[#34375B] fixed left-0 top-0 bottom-0 transition-all duration-200 overflow-hidden hidden md:block ${isCollapsed ? 'w-[60px]' : 'w-[268px]'
          }`}
        data-collapsed={isCollapsed}
      >
        <SidebarContent className="bg-[#8791f3] w-[268px]">
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu>
                <div className="flex items-center justify-between w-full">
                  <SidebarMenuItem className={isCollapsed ? 'hidden' : ''}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <SidebarMenuButton className="w-full">
                          <Globe className="w-4 h-4" />
                          <span className="transition-opacity duration-200 hover:text-[#5E64A7]">
                            {languages.find(lang => lang.code === i18n.language)?.name || t("common.language", "Language")}
                          </span>
                        </SidebarMenuButton>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start">
                        {languages.map(lang => (
                          <DropdownMenuItem key={lang.code} onClick={() => i18n.changeLanguage(lang.code)}>
                            {lang.name}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-white"
                      onClick={toggleCollapse}
                    >
                      {isCollapsed ? <ArrowRight className="h-4 w-4" /> : <ArrowLeft className="h-4 w-4" />}
                    </Button>
                  </SidebarMenuItem>
                </div>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>

          <SidebarGroup className="mt-4">
            <SidebarGroupContent>
              <SidebarMenu>
                {items.map(item => (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton
                      tooltip={item.title}
                      onClick={() => handleNavigation(item.url)}
                      className={`text-base hover:text-[#5E64A7] ${isCurrentRoute(item.url) ? 'text-[#34375B]' : ''}`}
                    >
                      <item.icon className="w-4 h-4" />
                      <span className={`transition-opacity duration-200 ${isCollapsed ? 'opacity-0 w-0' : ''}`}>
                        {item.title}
                      </span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>

        <SidebarFooter className="bg-[#8791f3]">
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu>
                {footerItems.map(item => (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton
                      tooltip={t(item.title)}
                      onClick={() => handleNavigation(item.url)}
                      className={`text-base hover:text-[#5E64A7] ${isCurrentRoute(item.url) ? 'text-[#34375B]' : ''}`}
                    >
                      <item.icon className="w-4 h-4" />
                      <span className={`transition-opacity duration-200 ${isCollapsed ? 'opacity-0 w-0' : ''}`}>
                        {t(item.title)}
                      </span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
                <SidebarMenuItem>
                  <SidebarMenuButton
                    tooltip={t("common.signOut", "Sign Out")}
                    onClick={handleSignOut}
                    className="text-base hover:text-[#5E64A7]"
                  >
                    <LogOut className="w-4 h-4" />
                    <span className={`transition-opacity duration-200 ${isCollapsed ? 'opacity-0 w-0' : ''}`}>
                      {t("common.signOut", "Sign Out")}
                    </span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarFooter>
      </Sidebar>

      {/* This div creates space for the sidebar in the layout */}
      <div className="hidden md:block fixed left-0 top-0 bottom-0" style={{ width: isCollapsed ? '60px' : '268px' }}></div>
    </>
  );
}
